import { Util } from 'src/shared/util/util';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';
import {
  EventTypes,
  OwnerBlock,
  Period,
  Properties,
  Property,
  Reservation,
  Job,
  Event,
  ClickAction,
  ClickTargets,
  AvailabilityRequest,
} from 'src/models/schedule-weekly-view.model';
import { PlanModel } from 'src/models/address.model';

import {
  DEFAULT_JOB_PADDING_TOP,
  getJobClearance,
  getOccupancyDepthOnDay,
  getOccupancyDepthStartingOnDay,
  layoutOccupancyLanes,
  OccupancyBar,
  OccupancyPlacement,
} from './occupancy-lanes';

// Extended interfaces for preprocessed data
interface ProcessedJob extends Job {
  formattedTitle?: string;
  formattedStatusName?: string;
  formattedStartDate?: string;
  formattedEndDate?: string;
  homekeeperNameUppercase?: string;
  statusInitialsUppercase?: string;
  truncatedEndTime?: string;
  width?: number;
  paddingTop?: number;
  cardStyle?: string;
  isBeingAssigned?: boolean;
  showProAssignmentMenu?: boolean;
  proInitialsStyles?: { [key: string]: string };
  avatarGradient?: string;
  footerTextStyles?: { [key: string]: string };
  showDifferentDates?: boolean;
  isInitiatedByHomekeeper?: boolean;
  showAllTimeProposals?: boolean;
  showViewAllTimesLink?: boolean;
  showAvailabilityStatus?: boolean;
}

interface ProcessedReservation extends Reservation {
  width?: number;
  cardStyle?: string;
  lane?: number;
}

interface ProcessedOwnerBlock extends OwnerBlock {
  width?: number;
  cardStyle?: string;
  lane?: number;
}

interface ProcessedAvailabilityRequest extends AvailabilityRequest {
  formattedTitle?: string;
  formattedStatusName?: string;
  parsedRequestTimesPreview?: string;
  width?: number;
  paddingTop?: number;
  cardStyle?: string;
}

interface ProcessedPlan extends PlanModel {
  formattedTitle?: string;
  frequencyLabel?: string;
  scheduleLabel?: string;
}

interface ProcessedProperty extends Property {
  displayName?: string;
  formattedNextReservationDate?: string;
  formattedNextJobDate?: string;
  isNextReservationToday?: boolean;
  isNextJobToday?: boolean;
  iconColor?: string | null;
  inquiriesLabel?: string;
  activePlans?: ProcessedPlan[];
  processedFilteredJobs?: ProcessedJob[];
  processedFilteredReservations?: ProcessedReservation[];
  processedFilteredAvailabilityRequests?: ProcessedAvailabilityRequest[];
  processedOwnerBlocks?: ProcessedOwnerBlock[];
  // Preprocessed event data for each day
  eventsByDay?: {
    [dayName: string]: {
      hasJobs: boolean;
      hasReservations: boolean;
      hasAvailabilityRequests: boolean;
      hasOwnerBlocks: boolean;
      jobs: ProcessedJob[];
      reservations: ProcessedReservation[];
      availabilityRequests: ProcessedAvailabilityRequest[];
      ownerBlocks: ProcessedOwnerBlock[];
    }
  };
}
import { MatMenuTrigger } from '@angular/material/menu';

import { BookJob } from 'src/providers/book-job/book-job';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Pros } from 'src/providers/pros/pros';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { BookService } from 'src/shared/services/book.service';
import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { BookJobPage } from 'src/pages/booking/book-job/book-job';
import { PlansPage } from 'src/pages/plans/plans';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Booking } from 'src/providers/booking/booking';
import { Schedule } from 'src/providers/schedule/schedule';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { WindowService } from 'src/shared/providers/window.service';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TruncatePipe } from 'src/shared/pipes/truncate.pipe';
import { TitleCasePipe } from '@angular/common';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { TagService } from 'src/shared/components/tag/tag.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  templateUrl: 'schedule-weekly-view.html',
  styleUrls: ['./schedule-weekly-view.scss'],
  selector: 'tidy-schedule-weekly-view',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ScheduleWeeklyViewComponent implements OnChanges, AfterViewInit {
  @ViewChild('assignProMenuTrigger') assignProMenuTrigger: MatMenuTrigger;
  @Input() isMobileResolution: boolean;
  @Input() selectedPeriodRange: Period;
  @Input() daysOfWeek: string[];
  @Input() properties: Properties;
  @Input() viewMode: string;
  @Input() showAboutThisPage: boolean;
  @Output() clickAction = new EventEmitter<ClickAction>();
  @Output() assignedPro = new EventEmitter<ClickAction>();
  /**
   * Emits true when the first-load settle finishes and the real grid is
   * showing (desktop only; the emission comes from the async settle timer so
   * it never mutates parent state mid change-detection). The host page keeps
   * the calendar skeleton mounted until this fires — and resets its own flag
   * when it starts a reload — so the skeleton survives the data-arrived
   * handoff without its float-in wave being cut short.
   */
  @Output() readinessChange = new EventEmitter<boolean>();
  today = LuxonDateTime.local().toISODate();
  private bookService = inject(BookService);

  // Processed data for template
  processedProperties: ProcessedProperty[] = [];
  processedDaysOfWeek: { day: string; translatedHeader: string; isToday: boolean; calendarDayClasses: string; isSortEnabledAsc: boolean; isSortDisabledAsc: boolean; isSortEnabledDesc: boolean; isSortDisabledDesc: boolean }[] = [];
  hasPrivatePros: boolean;
  sortProperty: string;
  sortDirection: 'asc' | 'desc';
  allProperties: Properties;
  wasCardClicked = false;
  @ViewChild('scrollableContainer', { static: true })
  scrollableContainer: ElementRef;
    privatePros: any;
  showAllTimesProposals: any[] = [];
  language: string | null;
  isContentVisible = true;

  // First-load gate (desktop): ngOnChanges rebuilds arrive in several async
  // passes on first load (the awaited pros check is a slow network call the
  // first time, so overlapping invocations can land seconds apart), and each
  // pass can change the precomputed event card widths. While 'pending' the
  // skeleton shows (it carries the float-in wave) and the real calendar is
  // display:none; once no rebuild is in flight AND the card widths are
  // unchanged between consecutive checks, the state flips to 'done' and the
  // real grid replaces the skeleton in place, instantly. 'done' then holds
  // until the rendered period or view mode changes (see
  // rearmEntranceOnPeriodChange), so route reuse keeping the instance still
  // means a revisit to the same week never reshows the skeleton.
  entranceState: 'pending' | 'done' = 'pending';
  private entranceSettleTimer: any;
  private pendingRebuilds = 0;
  private lastEntranceSignature: string | null = null;
  private entranceDeadline = 0;
  private lastEntranceKey: string | null = null;

  fixedColumnLeft = '0px';

  // Pipe instances for performance optimization
  private translationPipe = new TranslationPipe();
  private parseDateTimePipe = new ParseDateTimePipe();
  private truncatePipe = new TruncatePipe();
  private titleCasePipe = new TitleCasePipe();

  currentPage = 0;
  pageSize: number;

  get paginatedProperties(): ProcessedProperty[] {
    const start = this.currentPage * this.pageSize;
    return this.processedProperties.slice(start, start + this.pageSize);
  }

  get hasNextPage(): boolean {
    return (this.currentPage + 1) * this.pageSize < this.processedProperties.length;
  }

  get hasPriorPage(): boolean {
    return this.currentPage > 0;
  }

  goToNextPage() {
    if (this.hasNextPage) {
      this.currentPage++;
      this.scrollToTop();
      this.changeDetectorRef.markForCheck();
    }
  }

  goToPriorPage() {
    if (this.hasPriorPage) {
      this.currentPage--;
      this.scrollToTop();
      this.changeDetectorRef.markForCheck();
    }
  }

  scrollToTop() {
    if (this.scrollableContainer && this.scrollableContainer.nativeElement) {
      this.scrollableContainer.nativeElement.scrollTop = 0;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event) {
    const scrollEvent = {
      target: this.scrollableContainer.nativeElement,
    };
    this.updateFixedColumnLeft(scrollEvent);
  }

  constructor(
    public bookJobService: BookJob,
    private booking: Booking,
    private changeDetectorRef: ChangeDetectorRef,
    private client: Client,
    private navCtrl: CustomNavController,
    private pros: Pros,
    public util: Util,
    public schedule: Schedule,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService,
    public onboardingProvider: OnboardingProvider,
    private tagService: TagService,
    private destroyRef: DestroyRef
  ) {
    // Reflect tag colour changes (user picks a new house colour) on the tree
    // line + sub-icons for that property without waiting for a re-render.
    this.tagService.colorUpdates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((update) => {
        if (!update?.addressId) { return; }
        const target = this.processedProperties.find(
          (p) => String(p.id) === String(update.addressId)
        );
        if (!target) { return; }
        (target as any).color = update.color;
        target.iconColor = this.getPropertyIconColor(update.color);
        this.changeDetectorRef.markForCheck();
      });

    // The settle timer is now (re)armed on every ngOnChanges, not just while
    // the entrance gate is 'pending', so it can still be in flight at teardown.
    this.destroyRef.onDestroy(() => clearTimeout(this.entranceSettleTimer));
  }

  async ngAfterViewInit() {
    this.getPageSize();
    this.language = localStorage.getItem('language');
    this.privatePros = [];
    const allPros = this.showAboutThisPage ? [] : await this.pros.getAllPros();
    allPros.map((pro) => {
      if (pro?.is_private) {
        this.hasPrivatePros = true;
        this.privatePros.push({
          name: `${pro.first_name  } ${  pro.last_name}`,
          team_id: pro.main_team_id,
        });
      }
    });
  }

  getPageSize() {
    let totalCards = 0;
    this.properties.map(property => {
      totalCards += property.filteredJobs.length + property.filteredAvailabilityRequests.length;
    });
    this.pageSize = Math.floor(100 / (totalCards || 1) * this.properties.length);
  }

  showMore(): void {
    // Pagination replaces showMore; this is now a no-op or can be removed
  }

  updateFixedColumnLeft(_event: any) {
    this.fixedColumnLeft = '0px';
    this.changeDetectorRef.detectChanges();
  }

  async ngOnChanges(): Promise<void> {
    this.rearmEntranceOnPeriodChange();
    if (this.entranceState === 'pending' && !this.windowService.isDesktopRes) {
      this.entranceState = 'done';
    }
    this.pendingRebuilds++;
    // Preprocess days of week
    const showAboutThisPageForSchedule = localStorage.getItem('showAboutThisPageForSchedule') === 'true';
    if (!this.onboardingProvider.showOnboarding && !this.showAboutThisPage && !showAboutThisPageForSchedule) {
      this.hasPrivatePros = await this.pros.checkIfHasPrivatePro();
    }
    this.processedDaysOfWeek = this.daysOfWeek?.map(day => {
      const isToday = this.isToday(day);
      const calendarDayClasses = this.getCalendarDayClasses(day);
      const isSortEnabledAsc = this.sortDirection === 'asc' && this.sortProperty === day;
      const isSortDisabledAsc = this.sortProperty !== day || this.sortDirection !== 'asc';
      const isSortEnabledDesc = this.sortDirection === 'desc' && this.sortProperty === day;
      const isSortDisabledDesc = this.sortProperty !== day || this.sortDirection !== 'desc';
      return {
        day,
        translatedHeader: this.getTranslatedDayHeader(day),
        isToday,
        calendarDayClasses,
        isSortEnabledAsc,
        isSortDisabledAsc,
        isSortEnabledDesc,
        isSortDisabledDesc
      };
    }) || [];

    // Show all properties at once
    this.allProperties = this.properties;

    // Preprocess properties data
    this.processedProperties = this.preprocessProperties(this.properties || []);
    this.sortDirection = 'asc';
    this.sortProperty = this.daysOfWeek?.[0];
    this.currentPage = 0; // Reset to first page on data change

    // Trigger change detection for OnPush strategy
    this.changeDetectorRef.markForCheck();
    this.pendingRebuilds = Math.max(0, this.pendingRebuilds - 1);
    this.scheduleEntranceSettle();
  }

  // SEVEN_DAYS and WEEK_MON_SUN share one @if branch and one instance of this
  // component (schedule.html:91-103), so switching between them only rebinds
  // inputs — the component is never destroyed. Paging to another period behaves
  // the same way whenever the fetch resolves without a real network gap (the
  // demo fixture resolves synchronously, so the host's @if never flips off long
  // enough to tear us down). In both cases 'done' would stick, readinessChange
  // would never re-emit, and the host's isWeeklyViewReady would stay false —
  // leaving its skeleton up forever over a permanently display:none grid.
  // So re-arm the gate whenever the rendered period or view mode actually
  // changes. Keyed by value rather than reference, so a route revisit that
  // rebinds the same week keeps 'done' and still shows no skeleton.
  private rearmEntranceOnPeriodChange(): void {
    const key = `${this.viewMode}|${this.selectedPeriodRange?.startDate}|${this.selectedPeriodRange?.endDate}`;
    if (this.lastEntranceKey === key) {
      return;
    }
    const isFirstBind = this.lastEntranceKey === null;
    this.lastEntranceKey = key;
    if (isFirstBind) {
      return;
    }
    clearTimeout(this.entranceSettleTimer);
    this.entranceState = 'pending';
    this.entranceDeadline = 0;
    this.lastEntranceSignature = null;
  }

  // The reveal happens only when no async rebuild is in flight and the
  // precomputed card widths held steady across two consecutive checks — i.e.
  // reservation spans are final before the skeleton is swapped for the real
  // grid. Deadline guards against never revealing if the data keeps churning.
  private scheduleEntranceSettle(): void {
    if (!this.entranceDeadline) {
      this.entranceDeadline = Date.now() + 6000;
    }
    clearTimeout(this.entranceSettleTimer);
    this.entranceSettleTimer = setTimeout(() => {
      const signature = this.computeEntranceSignature();
      const isStable = this.pendingRebuilds === 0 && signature === this.lastEntranceSignature;
      this.lastEntranceSignature = signature;
      if (this.entranceState === 'pending' && !isStable && Date.now() < this.entranceDeadline) {
        this.scheduleEntranceSettle();
        return;
      }
      // Re-emitted even when already 'done'. The host resets its own
      // isWeeklyViewReady whenever it starts a reload (dismissAboutThisPage
      // reloads the same period with real data, so the re-arm above is not
      // triggered) and only this output can set it back — without an emit here
      // its skeleton would stay up over a grid that is ready to show.
      this.entranceState = 'done';
      this.readinessChange.emit(true);
      this.changeDetectorRef.markForCheck();
    }, 250);
  }

  private computeEntranceSignature(): string {
    return (this.processedProperties || [])
      .map((property) => Object.values(property.eventsByDay || {})
        .map((dayEvents: any) => [
          ...(dayEvents?.reservations || []),
          ...(dayEvents?.ownerBlocks || []),
          ...(dayEvents?.jobs || []),
        ].map((event: any) => event?.cardStyle || event?.width || '').join('|'))
        .join('|'))
      .join('||');
  }

    /**
   * Preprocess properties data to avoid repeated computations in template
   */
  private preprocessProperties(properties: Properties): ProcessedProperty[] {
    if (!properties) {return [];}

    return properties.map(property => {
      const ownerBlocks = property.ownerBlocks || [];
      const processedReservations = this.preprocessReservations(property.filteredReservations || [], ownerBlocks);
      const processedOwnerBlocks = this.preprocessOwnerBlocks(ownerBlocks, property.filteredReservations || []);
      const processedProperty: ProcessedProperty = {
        ...property,
        displayName: property?.name || property?.address || '',
        isNextReservationToday: property.nextReservationDate === this.today,
        isNextJobToday: property.nextJobDate === this.today,
        formattedNextReservationDate: property.nextReservationDate ? this.getNextReservationDate(property) : '',
        formattedNextJobDate: property.nextJobDate ? this.getNextJobDate(property) : '',
        iconColor: this.getPropertyIconColor((property as any)?.color),
        inquiriesLabel: property.inquiriesCount
          ? `${property.inquiriesCount} ${property.inquiriesCount === 1 ? 'inquiry' : 'inquiries'}`
          : '',
        activePlans: this.preprocessPlans(property.plans || []),
        processedFilteredReservations: processedReservations,
        processedOwnerBlocks,
      };
      const processedJobs = this.preprocessJobs(processedProperty.filteredJobs || []);
      const processedAvailabilityRequests = this.preprocessAvailabilityRequests(processedProperty.filteredAvailabilityRequests || []);
      processedProperty.processedFilteredJobs = processedJobs;
      processedProperty.processedFilteredAvailabilityRequests = processedAvailabilityRequests;
      processedProperty.eventsByDay = this.preprocessEventsByDay(processedJobs, processedReservations, processedAvailabilityRequests, property, processedOwnerBlocks);
      return processedProperty;
    });
  }

    /**
   * Preprocess jobs data
   */
  private preprocessJobs(jobs: Job[]): ProcessedJob[] {
    return jobs.map((job, _index) => {
      const width = this.getEventWidth(job, 'jobs');
      const processed: ProcessedJob = {
        ...job,
        formattedTitle: this.getFormattedJobTitle(job?.title),
        formattedStatusName: this.getFormattedStatusName(job?.status?.name),
        formattedStartDate: job?.booking?.startDateNoEarlierThan ?
          this.getFormattedDate(job.booking.startDateNoEarlierThan, 'MMM d') : '',
        formattedEndDate: job?.booking?.endDateNoLaterThan ?
          this.getFormattedDate(job.booking.endDateNoLaterThan, 'MMM d') : '',
        homekeeperNameUppercase: this.getHomekeeperNameUppercase(job?.homekeeperName),
        statusInitialsUppercase: this.getStatusInitialsUppercase(job?.status?.initials),
        truncatedEndTime: this.getTruncatedEndTime(job),
        width,
        // Do NOT calculate paddingTop or cardStyle here
        isBeingAssigned: this.bookJobService.isAssigningPrivatePro === String(job?.booking?.id),
        showProAssignmentMenu: this.hasPrivatePros &&
          !job?.title?.includes('2 Pros') &&
          job?.status?.name !== 'completed' &&
          job?.status?.name !== 'cancelled',
        proInitialsStyles: (() => {
          const styles: { [key: string]: string } = {};
          if (job?.status?.status === 'on_the_way' || job?.status?.status === 'arrived_at_home') {
            styles['margin-right'] = '8px';
          }
          return styles;
        })(),
        avatarGradient: this.util.getAvatarGradient(job?.homekeeperName || job?.status?.initials || 'BA'),
        footerTextStyles: { 'margin-left': job?.status?.class === 'pro-initials-in-progress' ? '5px' : '0px' },
        showDifferentDates: job?.booking?.startDateNoEarlierThan !== job?.booking?.endDateNoLaterThan,
        isInitiatedByHomekeeper: (job as any)?.initiatedBy === 'homekeeper',
        showAllTimeProposals: this.showAllTimesProposals.includes(String(job?.id)),
        showViewAllTimesLink: !this.showAllTimesProposals.includes(String(job?.id)) && (job?.parsedRequestTimes?.split('<br>').length > 2),
        showAvailabilityStatus: (job as any)?.initiatedBy !== 'homekeeper',
      };
      return processed;
    });
  }

  /**
   * Preprocess availability requests data
   */
  private preprocessAvailabilityRequests(requests: AvailabilityRequest[]): ProcessedAvailabilityRequest[] {
    return requests.map((request, _index) => {
      const width = this.getEventWidth(request, 'jobs');
      return {
        ...request,
        formattedTitle: this.getFormattedJobTitle(request?.title),
        formattedStatusName: this.getFormattedStatusName(request?.status?.name),
        parsedRequestTimesPreview: this.getParsedRequestTimesPreview(request),
        width,
        // Do NOT calculate paddingTop or cardStyle here
      };
    });
  }

  /**
   * Filter out one-time plans and decorate them with display-ready fields.
   * Each tile renders three lines: job type, frequency, and date+time.
   */
  private preprocessPlans(plans: PlanModel[]): ProcessedPlan[] {
    return (plans || [])
      .filter((plan) => plan?.plan_frequency && plan.plan_frequency !== 'once')
      .map((plan) => {
        const full = this.titleCasePipe.transform(plan?.formatted_plan_with_weekdays_and_times || '');
        const sepIndex = full.indexOf(' - ');
        const beforeSep = sepIndex >= 0 ? full.slice(0, sepIndex).trim() : full;
        const afterSep = sepIndex >= 0 ? full.slice(sepIndex + 3).trim() : '';

        const frequencyLabel = this.getPlanFrequencyLabel(plan?.plan_frequency);
        let jobType = beforeSep;
        if (frequencyLabel && beforeSep.toLowerCase().endsWith(frequencyLabel.toLowerCase())) {
          jobType = beforeSep.slice(0, beforeSep.length - frequencyLabel.length).trim();
        }

        // "Thursdays At 11:00 Am" -> "Thursdays at 11:00am"
        const scheduleLabel = afterSep
          .replace(/\bAt\b/g, 'at')
          .replace(/\s*\bAm\b/g, 'am')
          .replace(/\s*\bPm\b/g, 'pm');

        return {
          ...plan,
          formattedTitle: jobType,
          frequencyLabel,
          scheduleLabel,
        };
      });
  }

  /**
   * Mirrors the colour logic inside <tidy-tag> so tree icons can be tinted to
   * match the house. Returns null for "default" colours so the SCSS fallback
   * (var(--tidy-text-primary)) applies via the inline-style omission.
   */
  private getPropertyIconColor(color: string | undefined | null): string | null {
    if (!color || color === '#000000' || color === '#1E2125') {
      return null;
    }
    return color;
  }

  private getPlanFrequencyLabel(frequency: string): string {
    const map: { [key: string]: string } = {
      every_day: 'Every Day',
      every_week: 'Every Week',
      every_other_week: 'Every Other Week',
      twice_a_week: 'Twice A Week',
      thrice_a_week: 'Thrice A Week',
      every_four_weeks: 'Every Four Weeks',
    };
    if (map[frequency]) { return map[frequency]; }
    return this.titleCasePipe.transform((frequency || '').replace(/_/g, ' '));
  }


    /**
   * Preprocess reservations data. Owner blocks join the boundary scan so a
   * reservation ending the day a block starts (or vice versa) shares that
   * day cell half-and-half, like back-to-back reservations do.
   */
  private preprocessReservations(reservations: Reservation[], ownerBlocks: OwnerBlock[] = []): ProcessedReservation[] {
    const spans = this.buildOccupancySpans(reservations, ownerBlocks);
    const layout = this.buildOccupancyLayout(reservations, ownerBlocks);
    return reservations.map((reservation, _index) => {
      const width = this.getEventWidth(reservation, 'reservations');
      const { widthStyle, marginLeftStyle } = this.getBoundaryStyles(
        reservation.id,
        reservation.period?.startDate ?? reservation.startDate,
        reservation.period?.endDate ?? reservation.endDate,
        width,
        spans
      );
      const { lane, offset } = layout.get(this.occupancyKey('reservation', reservation.id)) ?? { lane: 0, offset: 0 };
      return {
        ...reservation,
        width,
        lane,
        cardStyle: `width: ${widthStyle}; margin-top: ${offset}px; margin-bottom: 5px; padding-right: 8px; top: 0;${marginLeftStyle}`,
      };
    });
  }

  /**
   * Preprocess owner blocks: same lane geometry as reservations (weekday
   * spans + boundary halving) so blocks slot into the calendar's occupancy
   * language without touching the job lanes.
   */
  private preprocessOwnerBlocks(ownerBlocks: OwnerBlock[], reservations: Reservation[]): ProcessedOwnerBlock[] {
    const spans = this.buildOccupancySpans(reservations, ownerBlocks);
    const layout = this.buildOccupancyLayout(reservations, ownerBlocks);
    return ownerBlocks.map((block) => {
      const width = this.getEventWidth(block as any, 'reservations');
      const { widthStyle, marginLeftStyle } = this.getBoundaryStyles(
        block.id,
        block.period?.startDate ?? block.startDate,
        block.period?.endDate ?? block.endDate,
        width,
        spans
      );
      const { lane, offset } = layout.get(this.occupancyKey('ownerBlock', block.id)) ?? { lane: 0, offset: 0 };
      return {
        ...block,
        width,
        lane,
        cardStyle: `width: ${widthStyle}; margin-top: ${offset}px; margin-bottom: 5px; padding-right: 8px; top: 0;${marginLeftStyle}`,
      };
    });
  }

  /**
   * Lane and margin-top for every occupancy bar in a property row; see
   * layoutOccupancyLanes for the lane rules.
   */
  private buildOccupancyLayout(
    reservations: Reservation[],
    ownerBlocks: OwnerBlock[]
  ): Map<string, OccupancyPlacement> {
    return layoutOccupancyLanes(this.toOccupancyBars(reservations, ownerBlocks));
  }

  /**
   * Every rendered occupancy bar in a property row, in render order: the
   * template stacks a day cell's bars in normal flow as reservations, then
   * owner blocks.
   */
  private toOccupancyBars(reservations: Reservation[], ownerBlocks: OwnerBlock[]): OccupancyBar[] {
    return [
      ...reservations.map((reservation) => this.toOccupancyBar('reservation', reservation)),
      ...ownerBlocks.map((block) => this.toOccupancyBar('ownerBlock', block)),
    ].filter((bar) => bar.start >= 0 && bar.end >= 0 && bar.rendered);
  }

  private toOccupancyBar(
    kind: 'reservation' | 'ownerBlock',
    event: Reservation | OwnerBlock
  ): { key: string; start: number; end: number; rendered: boolean } {
    return {
      key: this.occupancyKey(kind, event.id),
      start: this.daysOfWeek?.indexOf(event.startWeekday) ?? -1,
      end: this.daysOfWeek?.indexOf(event.endWeekday) ?? -1,
      // Matches the day-cell filter in preprocessEventsByDay: a bar whose
      // start falls outside the period isn't rendered, so it takes no lane.
      rendered: this.util.isDateInsidePeriod(event.startDate, this.selectedPeriodRange),
    };
  }

  // Reservations and owner blocks are separate id spaces, so the lane map is
  // keyed by both.
  private occupancyKey(kind: 'reservation' | 'ownerBlock', id: number | string): string {
    return `${kind}:${id}`;
  }

  /** padding-top for the first job card on a weekday so it clears every bar over that day. */
  private getOccupancyClearance(property: Property, weekday: string): number {
    const dayIndex = this.daysOfWeek?.indexOf(weekday) ?? -1;
    if (dayIndex < 0) {
      return DEFAULT_JOB_PADDING_TOP;
    }
    const bars = this.toOccupancyBars(property.filteredReservations || [], property.ownerBlocks || []);
    const layout = layoutOccupancyLanes(bars);
    return getJobClearance(
      getOccupancyDepthOnDay(bars, layout, dayIndex),
      getOccupancyDepthStartingOnDay(bars, layout, dayIndex)
    );
  }

  /** Real (unclamped) start/end for every occupancy span — reservations and owner blocks. */
  private buildOccupancySpans(
    reservations: Reservation[],
    ownerBlocks: OwnerBlock[]
  ): { id: number | string; start: string; end: string }[] {
    return [
      ...reservations.map((reservation) => ({
        id: reservation.id,
        start: reservation.period?.startDate ?? reservation.startDate,
        end: reservation.period?.endDate ?? reservation.endDate,
      })),
      ...ownerBlocks.map((block) => ({
        id: block.id,
        start: block.period?.startDate ?? block.startDate,
        end: block.period?.endDate ?? block.endDate,
      })),
    ];
  }

  private getBoundaryStyles(
    id: number | string,
    myStart: string,
    myEnd: string,
    width: number,
    spans: { id: number | string; start: string; end: string }[]
  ): { widthStyle: string; marginLeftStyle: string } {
    const endsBoundary = spans.some((other) => other.id !== id && other.start === myEnd);
    const startsBoundary = spans.some((other) => other.id !== id && other.end === myStart);
    let widthStyle = `${width}%`;
    let marginLeftStyle = '';
    if (endsBoundary && startsBoundary) {
      widthStyle = `calc(${width}% - 100%)`;
      marginLeftStyle = ' margin-left: 50%;';
    } else if (endsBoundary) {
      widthStyle = `calc(${width}% - 50%)`;
    } else if (startsBoundary) {
      widthStyle = `calc(${width}% - 50%)`;
      marginLeftStyle = ' margin-left: 50%;';
    }
    return { widthStyle, marginLeftStyle };
  }

    /**
   * Preprocess events by day to avoid repeated filtering and checking
   */
  private preprocessEventsByDay(
    jobs: ProcessedJob[],
    reservations: ProcessedReservation[],
    availabilityRequests: ProcessedAvailabilityRequest[],
    originalProperty: Property,
    ownerBlocks: ProcessedOwnerBlock[] = []
  ): { [dayName: string]: any } {
    const eventsByDay: { [dayName: string]: any } = {};

    if (!this.daysOfWeek) {return eventsByDay;}

    // Create lookup maps for jobs and availabilityRequests by id
    const jobsById = new Map(jobs.map(j => [j.id, j]));
    const availabilityRequestsById = new Map(availabilityRequests.map(a => [a.id, a]));

    this.daysOfWeek.forEach(day => {
      // Filter jobs for this day
      const dayJobs = jobs.filter(job =>
        job.startWeekday === day &&
        this.util.isDateInsidePeriod(job.startDate, this.selectedPeriodRange)
      ).map((job, index) => {
        const base = jobsById.get(job.id) || job;
        const width = this.getEventWidth(job, 'jobs');
        const paddingTop = this.getPaddingTop(job, originalProperty, 'jobs', index);
        return {
          ...base,
          width,
          paddingTop,
          cardStyle: `width: 103%; padding-top: ${paddingTop}px;`
        };
      });

      // Filter reservations for this day
      const dayReservations = reservations.filter(reservation =>
        reservation.startWeekday === day &&
        this.util.isDateInsidePeriod(reservation.startDate, this.selectedPeriodRange)
      ).map(reservation => ({
        ...reservation,
        width: this.getEventWidth(reservation, 'reservations')
      }));

      // Filter owner blocks for this day (cardStyle precomputed in preprocessOwnerBlocks)
      const dayOwnerBlocks = ownerBlocks.filter(block =>
        block.startWeekday === day &&
        this.util.isDateInsidePeriod(block.startDate, this.selectedPeriodRange)
      );

      // Filter availability requests for this day
      const dayAvailabilityRequests = availabilityRequests.filter(request =>
        request.startWeekday === day &&
        this.util.isDateInsidePeriod(request.startDate, this.selectedPeriodRange)
      ).map((request, index) => {
        const base = availabilityRequestsById.get(request.id) || request;
        const width = this.getEventWidth(request, 'jobs');
        const paddingTop = this.getPaddingTop(request, originalProperty, 'availabilityRequests', index);
        return {
          ...base,
          width,
          paddingTop,
          cardStyle: `width: ${width}%; padding-top: ${paddingTop}px;`
        };
      });

      eventsByDay[day] = {
        hasJobs: dayJobs.length > 0,
        hasReservations: dayReservations.length > 0,
        hasAvailabilityRequests: dayAvailabilityRequests.length > 0,
        hasOwnerBlocks: dayOwnerBlocks.length > 0,
        jobs: dayJobs,
        reservations: dayReservations,
        availabilityRequests: dayAvailabilityRequests,
        ownerBlocks: dayOwnerBlocks
      };
    });

    return eventsByDay;
  }

  getDayFromWeekBasedOnWeekday(weekday: string, format = 'MM-dd-yyyy'): string {
    const startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate);
    const endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate);

    // Check if this is the Mon-Sun week view (this will have exactly 7 days from Monday to Sunday)
    const _isMonSunWeek = this.viewMode === 'WEEK_MON_SUN' ||
                         (startDate.weekday === 1 && // Monday
                          endDate.weekday === 7 && // Sunday
                          endDate.diff(startDate, 'days').days === 6);

    for (let day = startDate; day <= endDate; day = day.plus({ days: 1 })) {
      if (day.toFormat('EEEE') === weekday) {
        if (this.language && this.language !== 'en') {
          const monthName = day.toFormat('MMMM');
          const translatedMonth = new TranslationPipe().transform(monthName);
          const dayNum = day.toFormat('d');
          return `${translatedMonth.slice(0,3)} ${dayNum}`;
        }
        return day.toFormat(format);
      }
    }
    return null;
  }

  getNextReservationDate(property) {
    const format = this.language && this.language !== 'en' ? 'MMMM d' : 'MMM d';
    const date = LuxonDateTime.fromFormat(property.nextReservationDate, 'yyyy-MM-dd').toFormat(format);
    if (this.language && this.language !== 'en') {
      const month = date.split(' ')[0];
      const day = date.split(' ')[1];
      return `${new TranslationPipe().transform(month).slice(0,3)} ${day}`;
    } else {
      return date;
    }
  }

  getNextJobDate(property) {
    const format = this.language && this.language !== 'en' ? 'MMMM d' : 'MMM d';
    const date = LuxonDateTime.fromFormat(property.nextJobDate, 'yyyy-MM-dd').toFormat(format);
    if (this.language && this.language !== 'en') {
      const month = date.split(' ')[0];
      const day = date.split(' ')[1];
      return `${new TranslationPipe().transform(month).slice(0,3)} ${day}`;
    } else {
      return date;
    }
  }

  hasEventAtProperty(
    day: string,
    property: ProcessedProperty,
    eventType: EventTypes
  ): boolean {
    // Use preprocessed data for better performance
    if (property.eventsByDay && property.eventsByDay[day]) {
      const dayEvents = property.eventsByDay[day];
      switch (eventType) {
        case 'filteredJobs':
          return dayEvents.hasJobs;
        case 'filteredReservations':
          return dayEvents.hasReservations;
        case 'filteredAvailabilityRequests':
          return dayEvents.hasAvailabilityRequests;
        default:
          return false;
      }
    }
    // Fallback to original logic if preprocessed data isn't available
    return !!this.getEventAtProperty(day, property, eventType);
  }

  daysBetweenWeekdays(startWeekday: string, endWeekday: string): number {
    const weekdays = this.daysOfWeek;
    const startIndex = weekdays.indexOf(startWeekday);
    const endIndex = weekdays.indexOf(endWeekday);
    if (startIndex === -1 || endIndex === -1) {
      return -1;
    }
    let daysDiff = endIndex - startIndex;
    if (daysDiff < 0) {
      daysDiff += 7;
    }
    return daysDiff;
  }

  getReservationAtPeriodLength(
    property: Property,
    event: Job | Reservation
  ): number | null {
    //TODO add better logic here to check the max number of reservations this proeprty has on a given day
    if (!property.filteredReservations || property.filteredReservations.length === 0) {
      return 0;
    }
    const reservations = property.filteredReservations.filter(
      (reservation) =>
        LuxonDateTime.fromISO(reservation.startDate) <=
          LuxonDateTime.fromISO(event.startDate) ||
        LuxonDateTime.fromISO(reservation.endDate) >=
          LuxonDateTime.fromISO(event.endDate)
    );
    return reservations.length;
  }

  hasJobAtPeriod(property: Property, event: Job | Reservation): Job | null {
    if (!property.filteredJobs || property.filteredJobs.length === 0) {
      return null;
    }
    return property.filteredJobs.find(
      (job) =>
        LuxonDateTime.fromISO(job.startDate) <=
          LuxonDateTime.fromISO(event.startDate) &&
        LuxonDateTime.fromISO(job.endDate) >=
          LuxonDateTime.fromISO(event.endDate) &&
        job.id !== event.id
    );
  }

  /**
   * Everything occupying a horizontal lane above the job cards: reservations
   * plus owner blocks. Job clearance (padding-top) must account for both so
   * job cards never render underneath a blocked-dates bar.
   */
  private getOccupancyEvents(property: Property): (Reservation | OwnerBlock)[] {
    return [
      ...(property.filteredReservations || []),
      ...(property.ownerBlocks || []),
    ];
  }

  someReservationStartsAtSameWeekday(
    property: Property,
    event: Job | AvailabilityRequest
  ): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0) {
      return false;
    }
    return occupancy.some(
      (reservation) =>
        reservation.startWeekday === event.startWeekday &&
        reservation.id !== event.id
    );
  }

  hasReservationBarExtendingThroughJobDay(
    property: Property,
    event: Job | AvailabilityRequest
  ): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0) {
      return false;
    }
    const jobIdx = this.daysOfWeek.indexOf(event.startWeekday);
    if (jobIdx < 0) {
      return false;
    }
    return occupancy.some((reservation) => {
      if (reservation.id === event.id) {
        return false;
      }
      const s = this.daysOfWeek.indexOf(reservation.startWeekday);
      const e = this.daysOfWeek.indexOf(reservation.endWeekday);
      if (s < 0 || e < 0) {
        return false;
      }
      const covered = s <= jobIdx && jobIdx <= e;
      const fromDifferentCell = reservation.startWeekday !== event.startWeekday;
      return covered && fromDifferentCell;
    });
  }

  someReservationEndsAtSameWeekday(
    property: Property,
    event: Job | AvailabilityRequest
  ): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0) {
      return false;
    }
    return occupancy.some(
      (reservation) =>
        reservation.endWeekday === event.endWeekday &&
        reservation.id !== event.id
    );
  }

  hasSomeReservationBetweenJobDay(
    property: Property,
    event: Job | AvailabilityRequest
  ): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0) {
      return false;
    }
    return occupancy.some((reservation) => {
      const reservationStart = LuxonDateTime.fromISO(reservation.startDate);
      const reservationEnd = LuxonDateTime.fromISO(reservation.endDate);
      const eventStart = LuxonDateTime.fromISO(event.startDate);
      const eventEnd = LuxonDateTime.fromISO(event.endDate);

      return (
        (reservationStart <= eventStart && reservationEnd >= eventStart) ||
        (reservationStart <= eventEnd && reservationEnd >= eventEnd) ||
        (reservationStart >= eventStart && reservationEnd <= eventEnd)
      );
    });
  }

  moreThanOneReservationEndsAtSameDay(
    property: Property,
    event: Job | AvailabilityRequest
  ): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0) {
      return false;
    }
    return (
      occupancy.filter(
        (reservation) =>
          reservation.endWeekday === event.endWeekday &&
          reservation.id !== event.id
      ).length > 1
    );
  }

  getPaddingTop(
    event: Job | AvailabilityRequest,
    property: Property,
    eventType: EventTypes,
    index: number
  ): number {
    // Later cards in a cell flow under the first one, which already cleared the bars.
    if (index > 0) {
      return 0;
    }
    return this.getOccupancyClearance(property, event.startWeekday);
  }

  twoOrMoreReservationsStartsOnTheFirstWeekDay(property: Property): boolean {
    const occupancy = this.getOccupancyEvents(property);
    if (occupancy.length === 0 || !this.daysOfWeek || this.daysOfWeek.length === 0) {
      return false;
    }
    return occupancy.filter(
      (reservation) => reservation.startWeekday === this.daysOfWeek[0]
    ).length > 1;
  }

  getEventAtProperty(
    day: string,
    property: ProcessedProperty,
    eventType: EventTypes
  ): Event {
    // Use processed data if available, otherwise fallback to original
    let eventsSource;
    if (eventType === 'filteredJobs') {
      eventsSource = property.processedFilteredJobs || property.filteredJobs;
    } else if (eventType === 'filteredReservations') {
      eventsSource = property.processedFilteredReservations || property.filteredReservations;
    } else if (eventType === 'filteredAvailabilityRequests') {
      eventsSource = property.processedFilteredAvailabilityRequests || property.filteredAvailabilityRequests;
    } else {
      eventsSource = property[eventType] as (Job | Reservation)[];
    }

    const eventsAtDay = eventsSource.filter(
      (event) =>
        event.startWeekday === day &&
        this.util.isDateInsidePeriod(event.startDate, this.selectedPeriodRange)
    );
    const event: Reservation | Job = eventsAtDay?.[0];
    if (!event) {
      return null;
    }
    const width = this.getEventWidth(event, eventType);
    return {
      ...event,
      width,
    };
  }

  getJobsAtProperty(day: string, property: ProcessedProperty): Event[] {
    // Use preprocessed data for better performance
    if (property.eventsByDay && property.eventsByDay[day]) {
      return property.eventsByDay[day].jobs as Event[];
    }

    // Fallback to original logic if preprocessed data isn't available
    const jobsSource = (property as ProcessedProperty).processedFilteredJobs || property.filteredJobs;
    const jobsAtDay = jobsSource.filter(
      (job) =>
        job.startWeekday === day &&
        this.util.isDateInsidePeriod(job.startDate, this.selectedPeriodRange)
    );
    return jobsAtDay.map((job, index) => {
      const width = this.getEventWidth(job, 'jobs');
      const paddingTop = this.getPaddingTop(job, property, 'jobs', index);
      return {
        ...job,
        width,
        paddingTop,
      };
    });
  }

  getAvailabilityRequestsAtProperty(day: string, property: ProcessedProperty): Event[] {
    // Use preprocessed data for better performance
    if (property.eventsByDay && property.eventsByDay[day]) {
      return property.eventsByDay[day].availabilityRequests as Event[];
    }

    // Fallback to original logic if preprocessed data isn't available
    const requestsSource = (property as ProcessedProperty).processedFilteredAvailabilityRequests || property.filteredAvailabilityRequests;
    const requestsAtDay = requestsSource.filter(
      (request) =>
        request.startWeekday === day &&
        this.util.isDateInsidePeriod(
          request.startDate,
          this.selectedPeriodRange
        )
    );
    return requestsAtDay.map((request, index) => {
      const width = this.getEventWidth(request, 'jobs');
      const paddingTop = this.getPaddingTop(
        request,
        property,
        'availabilityRequests',
        index
      );
      return {
        ...request,
        width,
        paddingTop,
      };
    });
  }

  getReservationsAtProperty(day: string, property: ProcessedProperty): Event[] {
    // Use preprocessed data for better performance
    if (property.eventsByDay && property.eventsByDay[day]) {
      return property.eventsByDay[day].reservations as Event[];
    }

    // Fallback to original logic if preprocessed data isn't available
    const reservationsSource = (property as ProcessedProperty).processedFilteredReservations || property.filteredReservations;
    const reservationsAtDay = reservationsSource.filter(
      (reservation) =>
        reservation.startWeekday === day &&
        this.util.isDateInsidePeriod(
          reservation.startDate,
          this.selectedPeriodRange
        )
    );
    return reservationsAtDay.map((reservation, _index) => {
      const width = this.getEventWidth(reservation, 'reservations');
      return {
        ...reservation,
        width,
      };
    });
  }

  /**
   * Track by function for ngFor loops to improve performance by not re-rendering the DOM elements
   * @param index
   * @param item
   * @returns
   */
  trackByFn(index, item) {
    return item.id;
  }

  /**
   * Track by function for properties
   */
  trackByPropertyFn(index: number, property: ProcessedProperty) {
    return property.id;
  }

  /**
   * Track by function for days
   */
  trackByDayFn(index: number, dayData: any) {
    return dayData.day;
  }

  // Template optimization methods

  /**
   * Get formatted day header for display
   */
  getDayHeader(day: string): string {
    const translatedDay = day; // assuming translation pipe is handled elsewhere
    const dayDate = this.getDayFromWeekBasedOnWeekday(day, 'MMM d');
    return `${translatedDay.slice(0, 3)} ${dayDate}`;
  }

  /**
   * Check if sorting is enabled for a given day and direction
   */
  isSortEnabled(day: string, direction: 'asc' | 'desc'): boolean {
    return this.sortDirection === direction && this.sortProperty === day;
  }

  /**
   * Check if sorting is disabled for a given day and direction
   */
  isSortDisabled(day: string, direction: 'asc' | 'desc'): boolean {
    return this.sortProperty !== day || this.sortDirection !== direction;
  }

  /**
   * Get CSS classes for sort buttons
   */
  getSortClasses(day: string, direction: 'asc' | 'desc'): { [key: string]: boolean } {
    return {
      'sort-enabled': this.isSortEnabled(day, direction),
      'sort-disabled': this.isSortDisabled(day, direction)
    };
  }

  /**
   * Get CSS classes for calendar day
   */
  getCalendarDayClasses(day: string): string {
    const isCurrentDay = this.getIsCurrentDay(this.getDayFromWeekBasedOnWeekday(day));
    return isCurrentDay ? 'calendar-day current-day' : 'calendar-day';
  }

  /**
   * Check if next reservation date is today
   */
  isNextReservationToday(property: Property): boolean {
    return property.nextReservationDate === this.today;
  }

  /**
   * Check if next job date is today
   */
  isNextJobToday(property: Property): boolean {
    return property.nextJobDate === this.today;
  }

  /**
   * Get card container styles for reservation
   */
  getReservationCardStyle(reservation: any): string {
    return `width: ${reservation?.width}%;`;
  }

  /**
   * Get card container styles for job
   */
  getJobCardStyle(job: any): string {
    return `width: 103%; padding-top: ${job?.paddingTop}px;`;
  }

  /**
   * Get card container styles for availability request
   */
  getAvailabilityRequestCardStyle(job: any): string {
    return `width: ${job?.width}%; padding-top: ${job?.paddingTop}px;`;
  }

  /**
   * Check if should show private pro assignment menu
   */
  shouldShowProAssignmentMenu(job: any): boolean {
    return this.hasPrivatePros &&
           !job?.title?.includes('2 Pros') &&
           job?.status?.name !== 'completed' &&
           job?.status?.name !== 'cancelled';
  }

  /**
   * Check if job is currently being assigned
   */
  isJobBeingAssigned(job: any): boolean {
    return this.bookJobService.isAssigningPrivatePro === job?.booking?.id;
  }

  /**
   * Get footer text styles for job status
   */
  getFooterTextStyles(job: any): { [key: string]: string } {
    const marginLeft = job?.status?.class === 'pro-initials-in-progress' ? '5px' : '0px';
    return { 'margin-left': marginLeft };
  }

  /**
   * Get styles for pro initials
   */
  getProInitialsStyles(job: any): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    if (job?.status?.status === 'on_the_way' || job?.status?.status === 'arrived_at_home') {
      styles['margin-right'] = '8px';
    }
    return styles;
  }

  /**
   * Check if should show different date for job request
   */
  shouldShowDifferentDates(job: any): boolean {
    return job?.booking?.startDateNoEarlierThan !== job?.booking?.endDateNoLaterThan;
  }

  /**
   * Get truncated end time for job request
   */
  getTruncatedEndTime(job: any): string {
    if (this.shouldShowDifferentDates(job)) {
      return job?.booking?.endTimeNoLaterThan;
    }
    return job?.booking?.endTimeNoLaterThan?.substring(0, 16) || '';
  }

  /**
   * Check if availability request was initiated by homekeeper
   */
  isInitiatedByHomekeeper(job: any): boolean {
    return (job as any)?.initiatedBy === 'homekeeper';
  }

  /**
   * Check if should show all time proposals for a job
   */
  shouldShowAllTimeProposals(jobId: string): boolean {
    return this.showAllTimesProposals.includes(jobId);
  }

  /**
   * Get parsed request times (first two entries)
   */
  getParsedRequestTimesPreview(job: any): string {
    return job?.parsedRequestTimes?.split('<br><br>', 2)[0] || '';
  }

  /**
   * Check if should show "View All Times" link
   */
  shouldShowViewAllTimesLink(job: any): boolean {
    return !this.shouldShowAllTimeProposals(job?.id) &&
           (job?.parsedRequestTimes?.split('<br>').length > 2);
  }

  /**
   * Check if should show status for availability request
   */
  shouldShowAvailabilityStatus(job: any): boolean {
    return (job as any)?.initiatedBy !== 'homekeeper';
  }

  // Private helper methods for preprocessing

  /**
   * Get translated and formatted day header (used during preprocessing)
   */
  private getTranslatedDayHeader(day: string): string {
    const translatedDay = this.translationPipe.transform(day);
    const dayDate = this.getDayFromWeekBasedOnWeekday(day, 'MMM d');
    return `${translatedDay.slice(0, 3)} ${dayDate}`;
  }

  /**
   * Get formatted job title (titlecase + truncate) (used during preprocessing)
   */
  private getFormattedJobTitle(title: string, limit = 22): string {
    if (!title) {return '';}
    const titleCased = this.titleCasePipe.transform(title);
    return this.truncatePipe.transform(titleCased, limit, false, '');
  }

  /**
   * Get formatted status name (titlecase) (used during preprocessing)
   */
  private getFormattedStatusName(statusName: string): string {
    if (!statusName) {return '';}
    return this.titleCasePipe.transform(statusName);
  }

  /**
   * Get formatted date string using parseDateTime pipe (used during preprocessing)
   */
  private getFormattedDate(date: string, format: string): string {
    if (!date) {return '';}
    return this.parseDateTimePipe.transform(date, format);
  }

  /**
   * Get uppercased homekeeper name or default (used during preprocessing)
   */
  private getHomekeeperNameUppercase(homekeeperName: string): string {
    return (homekeeperName || 'BA').toUpperCase();
  }

  /**
   * Get uppercased status initials (used during preprocessing)
   */
  private getStatusInitialsUppercase(initials: string): string {
    return (initials || '').toUpperCase();
  }

  getEventWidth(event: Reservation | Job, eventType: EventTypes): number {
    const weekdays = this.daysOfWeek;
    const startIndex = weekdays.indexOf(event.startWeekday);
    const endIndex = weekdays.indexOf(event.endWeekday);
    const daysDiff = endIndex - startIndex;
    const cardWidth = eventType === 'jobs' ? 108 : 108;

    // Fixed width adjustments for widest screen
    const widthAdjustments = {
      7: -23,
      6: -20,
      5: -17,
      4: -13,
      3: -10,
      2: -7,
      1: -4,
    };

    if (daysDiff < 0) {
      const lastWeekday = weekdays[weekdays.length - 1];
      const lastWeekdayDaysDiff = this.daysBetweenWeekdays(
        event.startWeekday,
        lastWeekday
      );
      return (
        cardWidth * lastWeekdayDaysDiff +
        cardWidth +
        (widthAdjustments[lastWeekdayDaysDiff] || 0)
      );
    }

    const totalDaysInWeek = 7;
    const eventDurationInDays =
      this.daysBetweenWeekdays(event.startWeekday, event.endWeekday) + 1;
    const eventWidthUnits =
      (eventDurationInDays / totalDaysInWeek) * cardWidth * 7;

    return eventWidthUnits + (widthAdjustments[eventDurationInDays] || 0);
  }

  emitClickAction(
    target: ClickTargets,
    property: Property,
    eventType?: EventTypes,
    event?: Event
  ): void {
    localStorage.setItem('addressId', property.id.toString());
    this.wasCardClicked = true;
    this.clickAction.emit({
      target,
      eventType,
      property,
      event,
    });
  }

  emitInquiriesClick(property: Property): void {
    localStorage.setItem('addressId', property.id.toString());
    this.wasCardClicked = true;
    this.clickAction.emit({
      target: 'inquiries',
      property,
    });
  }

  emitPlanClick(property: Property, plan: PlanModel): void {
    localStorage.setItem('addressId', property.id.toString());
    this.wasCardClicked = true;
    this.clickAction.emit({
      target: 'plan',
      property,
      plan,
    });
  }

  sort(weekDay: string, sortDirection: 'asc' | 'desc'): void {
    this.sortDirection = sortDirection;
    this.sortProperty = weekDay;
    this.allProperties.sort((a: Property, b: Property) => {
      // For sorting, we still use the original properties since we need to sort the raw data
      const aHasEvent =
        (a.filteredJobs || []).some(job => job.startWeekday === weekDay && this.util.isDateInsidePeriod(job.startDate, this.selectedPeriodRange)) ||
        (a.filteredReservations || []).some(reservation => reservation.startWeekday === weekDay && this.util.isDateInsidePeriod(reservation.startDate, this.selectedPeriodRange)) ||
        (a.filteredAvailabilityRequests || []).some(request => request.startWeekday === weekDay && this.util.isDateInsidePeriod(request.startDate, this.selectedPeriodRange));
      const bHasEvent =
        (b.filteredJobs || []).some(job => job.startWeekday === weekDay && this.util.isDateInsidePeriod(job.startDate, this.selectedPeriodRange)) ||
        (b.filteredReservations || []).some(reservation => reservation.startWeekday === weekDay && this.util.isDateInsidePeriod(reservation.startDate, this.selectedPeriodRange)) ||
        (b.filteredAvailabilityRequests || []).some(request => request.startWeekday === weekDay && this.util.isDateInsidePeriod(request.startDate, this.selectedPeriodRange));
      if (aHasEvent && bHasEvent) {
        return 0;
      } else if (aHasEvent) {
        return sortDirection === 'asc' ? -1 : 1;
      } else if (bHasEvent) {
        return sortDirection === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });
    this.processedProperties = this.preprocessProperties(this.allProperties);
    // Update sort state on processedDaysOfWeek
    this.processedDaysOfWeek = this.daysOfWeek?.map(day => {
      const isToday = this.isToday(day);
      const calendarDayClasses = this.getCalendarDayClasses(day);
      const isSortEnabledAsc = this.sortDirection === 'asc' && this.sortProperty === day;
      const isSortDisabledAsc = this.sortProperty !== day || this.sortDirection !== 'asc';
      const isSortEnabledDesc = this.sortDirection === 'desc' && this.sortProperty === day;
      const isSortDisabledDesc = this.sortProperty !== day || this.sortDirection !== 'desc';
      return {
        day,
        translatedHeader: this.getTranslatedDayHeader(day),
        isToday,
        calendarDayClasses,
        isSortEnabledAsc,
        isSortDisabledAsc,
        isSortEnabledDesc,
        isSortDisabledDesc
      };
    }) || [];
    
    // Trigger change detection for OnPush strategy
    this.changeDetectorRef.markForCheck();
  }

  getIsCurrentDay(day) {
    return day === new ParseDateTimePipe().transform(this.today, 'MM-dd-yyyy');
  }

  isToday(weekday: string): boolean {
    const today = LuxonDateTime.local();
    const todayWeekday = today.toFormat('EEEE');

    if (weekday !== todayWeekday) {
      return false;
    }

    const startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate);
    const endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate);
    const todayDate = today.toISODate();

    for (let day = startDate; day <= endDate; day = day.plus({ days: 1 })) {
      if (day.toFormat('EEEE') === weekday) {
        return day.toISODate() === todayDate;
      }
    }

    return false;
  }

  async goToBookjob(day, property) {
    this.bookJobService.clearBookJobStorage();
    const wasCardClicked = await new Promise((resolve) =>
      setTimeout(() => {
        if (this.wasCardClicked) {
          resolve(this.wasCardClicked);
          return;
        }
        resolve(null);
      }, 100)
    );
    if (wasCardClicked) {
      this.wasCardClicked = false;
      return;
    }
    const date = this.getDayFromWeekBasedOnWeekday(day);
    await this.storage.save('bookJobBackPage', 'schedule');
    await this.client.getClientInfo();
    const addresses = await this.client.getMoreDetailAddresses();
    const addressId = property.id;
    localStorage.setItem('addressId', addressId.toString());
    const address = addresses.find((ad) => ad.id === addressId);
    const bookingType = 'add_job';
    const params = {
      address,
      bookingType,
      date,
      providedStartDate: date,
    };
    await this.bookService.goToBookJob(params);
  }

  private navigateToPlansPage(address: any, plans: any, addressFilter: any): void {
    const plansParams = { address, plans, addressFilter };
    if (!this.isMobileResolution) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', plansParams);
      this.rightSidePanelService.openRightSidePanel(PlansPage);
    } else {
      this.navCtrl.navigateForward('plans', plansParams);
    }
  }

  async assignPro(job, pro, _event, _index) {
    try {
      this.assignProMenuTrigger.closeMenu();
      this.changeDetectorRef.detectChanges();
      this.bookJobService.isAssigningPrivatePro = job?.booking?.id;
      localStorage.setItem('proName', pro.name);
      await this.booking.assignPrivatePro(job?.booking?.id, pro?.team_id);
    } catch (err) {
      localStorage.setItem('assignProError', 'true');
      const errorMessage =
        err.error && err.error.message ? err.error.message : err.message;
      this.util.showError(errorMessage);
    }
    this.assignedPro.emit(job);
  }

  async showProMenu(event) {
    event.stopPropagation();
  }

  viewAllRequestTimes(_job, _event) {
    _event.stopPropagation();
    this.showAllTimesProposals.push(_job?.id);
    this.changeDetectorRef.detectChanges();
  }
}
